import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface City {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  country?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

interface Country {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CityDialogProps {
  open: boolean;
  city: City | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface CityFormData {
  name: string;
  countryId: string;
  active: boolean;
}

const CityDialog: React.FC<CityDialogProps> = ({
  open,
  city,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CityFormData>({
    name: '',
    countryId: '',
    active: true
  });
  const [errors, setErrors] = useState<Partial<CityFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const dispatch = useAppDispatch();

  const isEditing = !!city;

  // Fetch countries when dialog opens
  useEffect(() => {
    if (open && countries.length === 0) {
      fetchCountries();
    }
  }, [open]);

  // Reset form when dialog opens/closes or city changes
  useEffect(() => {
    if (open) {
      if (city) {
        setFormData({
          name: city.name,
          countryId: city.country?.id || '',
          active: city.active
        });
      } else {
        setFormData({
          name: '',
          countryId: '',
          active: true
        });
      }
      setErrors({});
    }
  }, [open, city]);

  // Fetch countries function
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await DataService.get('api/Country/all');
      if (response.ok) {
        const responseData = await response.json();
        let rawCountries: any[] = [];

        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          rawCountries = responseData.data;
        } else if (Array.isArray(responseData)) {
          rawCountries = responseData;
        }

        const countriesData: Country[] = rawCountries.map(country => ({
          id: country.id,
          name: country.name,
          code: country.isoCode || country.code,
          isActive: country.active !== undefined ? country.active : country.isActive,
          createdAt: country.createdAt || new Date().toISOString(),
          updatedAt: country.updatedAt || new Date().toISOString()
        }));

        // Only show active countries
        setCountries(countriesData.filter(country => country.isActive));
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to load countries. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setLoadingCountries(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof CityFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Handle select changes
  const handleSelectChange = (field: keyof CityFormData) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user selects
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<CityFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'City name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'City name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'City name cannot exceed 100 characters';
    }

    if (!formData.countryId) {
      newErrors.countryId = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        countryId: formData.countryId,
        ...(isEditing && { id: city.id, active: formData.active })
      };

      const endpoint = isEditing ? 'api/City/update' : 'api/City/create';
      const response = await DataService.post(endpoint, payload);

      if (response.ok) {
        onSuccess();
      } else {
        // Try to get error message from response
        let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} city`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Use default error message
        }

        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: `Network error. Please check your connection and try again.`,
          type: 'error'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit City' : 'Add New City'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? 'Update the city information below'
              : 'Fill in the details to create a new city'
            }
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="City Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
            fullWidth
            placeholder="Enter city name (e.g., New York, London, Tokyo)"
            variant="outlined"
          />

          <FormControl fullWidth variant="outlined" error={!!errors.countryId}>
            <InputLabel>Country *</InputLabel>
            <Select
              value={formData.countryId}
              onChange={handleSelectChange('countryId')}
              label="Country *"
              disabled={submitting || loadingCountries}
            >
              <MenuItem value="">
                <em>Select a country</em>
              </MenuItem>
              {countries.map(country => (
                <MenuItem key={country.id} value={country.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {country.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      {country.code}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.countryId && (
              <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                {errors.countryId}
              </Typography>
            )}
            {loadingCountries && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mt: 0.5 }}>
                Loading countries...
              </Typography>
            )}
          </FormControl>

          {isEditing && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleInputChange('active')}
                  disabled={submitting}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.active
                      ? 'This city is currently active and available for use'
                      : 'This city is inactive and will not be available for selection'
                    }
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? `${isEditing ? 'Updating' : 'Creating'}...`
              : `${isEditing ? 'Update' : 'Create'} City`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CityDialog;